import type {
  Charge,
  CheckoutParams,
  MuPagCallbacks,
  MuPagEnvironment,
  MuPagLocale,
  MuPagMessage,
  MuPagOptions,
  MuPagTheme,
  MuPagWidgetError,
} from "./types.js";

export type {
  Charge,
  CheckoutCustomer,
  CheckoutItem,
  CheckoutParams,
  MuPagCallbacks,
  MuPagEnvironment,
  MuPagLocale,
  MuPagOptions,
  MuPagTheme,
  MuPagMessage,
  MuPagWidgetError,
} from "./types.js";

const VERSION = "0.1.1";
const API_BASE_URLS: Record<MuPagEnvironment, string> = {
  test: "https://api.sandbox.mupag.com.br",
  prd: "https://api.mupag.com.br",
};
const CHECKOUT_BASE_URLS: Record<MuPagEnvironment, string> = {
  test: "https://checkout.sandbox.mupag.com.br",
  prd: "https://checkout.mupag.com.br",
};
const DEFAULT_IFRAME_TIMEOUT_MS = 8_000;
const DEFAULT_REQUEST_TIMEOUT_MS = 15_000;
const DEFAULT_MAX_RESPONSE_BYTES = 256 * 1024;
const MAX_REQUEST_BYTES = 1024 * 1024;
const MAX_MONEY_CENTS = 9_000_000_000_000_000;
const DATA_BOUND = "mupagWidgetBound";

interface ActiveModal {
  host: HTMLElement;
  iframe: HTMLIFrameElement;
  checkoutOrigin: string;
  callbacks: MuPagCallbacks;
  fallbackUrl: string;
  loaded: boolean;
  loadTimer: number | undefined;
}

function trimSlash(value: string): string {
  return value.replace(/\/+$/, "");
}

function resolveLocale(locale: MuPagOptions["locale"]): MuPagLocale {
  if (locale === "pt-BR" || locale === "en-US") {
    return locale;
  }
  if (typeof navigator !== "undefined" && navigator.language.toLowerCase().startsWith("en")) {
    return "en-US";
  }
  return "pt-BR";
}

function appendWidgetParams(url: URL, options: Required<Pick<MuPagOptions, "checkoutBaseUrl">> & MuPagOptions): URL {
  const locale = resolveLocale(options.locale);
  url.searchParams.set("locale", locale);
  if (options.theme && Object.keys(options.theme).length > 0) {
    url.searchParams.set("theme", JSON.stringify(options.theme));
  }
  if (typeof window !== "undefined" && window.location?.origin) {
    url.searchParams.set("parent_origin", window.location.origin);
  }
  return url;
}

function createModal(url: string, theme: MuPagTheme | undefined, onClose: () => void): { host: HTMLElement; iframe: HTMLIFrameElement } {
  const host = document.createElement("div");
  host.dataset.mupagWidgetRoot = "true";
  const root = host.attachShadow({ mode: "open" });
  const overlay = document.createElement("div");
  overlay.setAttribute("part", "overlay");
  const dialog = document.createElement("section");
  dialog.setAttribute("part", "dialog");
  dialog.setAttribute("role", "dialog");
  dialog.setAttribute("aria-modal", "true");
  dialog.setAttribute("aria-label", "Checkout seguro MuPag");
  const close = document.createElement("button");
  close.type = "button";
  close.textContent = "Fechar";
  close.setAttribute("part", "close");
  close.addEventListener("click", onClose);
  const iframe = document.createElement("iframe");
  iframe.src = url;
  iframe.title = "Checkout seguro MuPag";
  iframe.allow = "payment *";
  iframe.referrerPolicy = "strict-origin-when-cross-origin";
  iframe.setAttribute("part", "iframe");
  iframe.setAttribute("loading", "eager");
  iframe.setAttribute(
    "sandbox",
    "allow-forms allow-scripts allow-same-origin allow-popups allow-top-navigation-by-user-activation",
  );
  dialog.append(close, iframe);
  overlay.append(dialog);

  const primary = theme?.primaryColor ?? "#176bff";
  const radius = theme?.borderRadius ?? "18px";
  const font = theme?.fontFamily ?? "Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, Segoe UI, sans-serif";
  const css = `
    :host{all:initial;--gw-primary:${primary};--gw-radius:${radius};font-family:${font}}
    [part=overlay]{position:fixed;inset:0;z-index:2147483647;display:flex;align-items:center;justify-content:center;background:rgba(10,18,32,.54);padding:24px}
    [part=dialog]{position:relative;width:min(100%,760px);height:min(92vh,860px);overflow:hidden;border-radius:var(--gw-radius);background:#fff;box-shadow:0 24px 80px rgba(10,18,32,.28)}
    [part=close]{position:absolute;right:12px;top:10px;z-index:2;border:1px solid rgba(15,23,42,.16);border-radius:999px;background:#fff;color:#243044;font:600 13px/1.2 inherit;padding:8px 12px;cursor:pointer}
    [part=close]:focus{outline:3px solid color-mix(in srgb,var(--gw-primary) 35%,transparent);outline-offset:2px}
    [part=iframe]{display:block;width:100%;height:100%;border:0;background:#fff}
    @media (max-width:640px){[part=overlay]{align-items:stretch;padding:0;background:#fff}[part=dialog]{width:100%;height:100vh;max-height:none;border-radius:0;box-shadow:none}[part=close]{right:10px;top:8px}}
  `;
  if ("adoptedStyleSheets" in root && "CSSStyleSheet" in window) {
    const sheet = new CSSStyleSheet();
    sheet.replaceSync(css);
    root.adoptedStyleSheets = [sheet];
    root.append(overlay);
  } else {
    const style = document.createElement("style");
    style.textContent = css;
    root.append(style, overlay);
  }
  document.body.append(host);
  return { host, iframe };
}

function isMuPagMessage(data: unknown): data is MuPagMessage {
  if (!data || typeof data !== "object" || !("type" in data) || typeof data.type !== "string") {
    return false;
  }
  return ["mupag:loaded", "mupag:payment_completed", "mupag:close", "mupag:error"].includes(data.type);
}

export class MuPag {
  private readonly options: Required<
    Pick<
      MuPagOptions,
      | "checkoutBaseUrl"
      | "apiBaseUrl"
      | "fetch"
      | "fallbackRedirect"
      | "iframeLoadTimeoutMs"
      | "requestTimeoutMs"
      | "maxResponseBytes"
    >
  > &
    MuPagOptions;
  private active: ActiveModal | null = null;

  constructor(options: MuPagOptions) {
    validateOptions(options);
    this.options = {
      ...options,
      checkoutBaseUrl: allowedOrigin(
        options.checkoutBaseUrl ?? CHECKOUT_BASE_URLS[options.environment],
        CHECKOUT_BASE_URLS[options.environment],
        options.environment,
        "checkoutBaseUrl",
      ),
      apiBaseUrl: allowedOrigin(
        options.apiBaseUrl ?? API_BASE_URLS[options.environment],
        API_BASE_URLS[options.environment],
        options.environment,
        "apiBaseUrl",
      ),
      fetch: options.fetch ?? globalThis.fetch.bind(globalThis),
      fallbackRedirect: options.fallbackRedirect ?? true,
      iframeLoadTimeoutMs: options.iframeLoadTimeoutMs ?? DEFAULT_IFRAME_TIMEOUT_MS,
      requestTimeoutMs: options.requestTimeoutMs ?? DEFAULT_REQUEST_TIMEOUT_MS,
      maxResponseBytes: options.maxResponseBytes ?? DEFAULT_MAX_RESPONSE_BYTES,
    };
    this.handleMessage = this.handleMessage.bind(this);
  }

  async openCheckout(params: CheckoutParams): Promise<void> {
    if (!params || typeof params !== "object" || Array.isArray(params)) {
      throw new Error("Checkout params must be an object.");
    }
    const { onSuccess, onClose, onError, idempotencyKey, ...body } = params;
    validateCheckout(body, this.options.environment);
    let encodedBody: string;
    let canonicalBody: typeof body;
    try {
      encodedBody = JSON.stringify(body);
      canonicalBody = JSON.parse(encodedBody) as typeof body;
    } catch {
      throw new Error("Checkout payload must be valid JSON.");
    }
    validateCheckout(canonicalBody, this.options.environment);
    if (new TextEncoder().encode(encodedBody).byteLength > MAX_REQUEST_BYTES) {
      throw new Error("Checkout payload exceeds the safe 1 MiB limit.");
    }
    const key = idempotencyKey === undefined ? createIdempotencyKey() : validateIdempotencyKey(idempotencyKey);
    const controller = new AbortController();
    const timer = window.setTimeout(() => controller.abort(), this.options.requestTimeoutMs);
    let response: Response;
    try {
      response = await this.options.fetch(`${this.options.apiBaseUrl}/v1/checkout-sessions`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `Bearer ${this.options.publishableKey}`,
          "Idempotency-Key": key,
        },
        body: encodedBody,
        signal: controller.signal,
      });
    } catch {
      const error = { code: "checkout_network_error", message: "Checkout session request failed." };
      onError?.(error);
      throw new Error(error.message);
    } finally {
      window.clearTimeout(timer);
    }
    const responseBody = await readBoundedJson(response, this.options.maxResponseBytes);
    if (!response.ok) {
      const error = { code: "checkout_session_failed", message: `Checkout session failed (${response.status}).` };
      onError?.(error);
      throw new Error(error.message);
    }
    const payload = checkoutResponse(responseBody);
    const checkoutUrl = safeCheckoutUrl(payload.url, payload.id, this.options.checkoutBaseUrl);
    const url = appendWidgetParams(checkoutUrl, this.options);
    const callbacks: MuPagCallbacks = {};
    if (onSuccess) {
      callbacks.onSuccess = onSuccess;
    }
    if (onClose) {
      callbacks.onClose = onClose;
    }
    if (onError) {
      callbacks.onError = onError;
    }
    this.openUrl(url.toString(), callbacks);
  }

  close(): void {
    if (!this.active) {
      return;
    }
    if (this.active.loadTimer) {
      window.clearTimeout(this.active.loadTimer);
    }
    window.removeEventListener("message", this.handleMessage);
    this.active.host.remove();
    this.active = null;
  }

  private openUrl(url: string, callbacks: MuPagCallbacks): void {
    this.close();
    const checkoutOrigin = new URL(url).origin;
    const modal = createModal(url, this.options.theme, () => {
      callbacks.onClose?.();
      this.close();
    });
    const active: ActiveModal = {
      ...modal,
      checkoutOrigin,
      callbacks,
      fallbackUrl: url,
      loaded: false,
      loadTimer: undefined,
    };
    active.iframe.addEventListener("load", () => {
      active.loaded = true;
      if (active.loadTimer) {
        window.clearTimeout(active.loadTimer);
      }
    });
    active.loadTimer = window.setTimeout(() => {
      if (!active.loaded && this.options.fallbackRedirect) {
        window.location.assign(active.fallbackUrl);
      }
    }, this.options.iframeLoadTimeoutMs);
    this.active = active;
    window.addEventListener("message", this.handleMessage);
  }

  private handleMessage(event: MessageEvent): void {
    if (
      !this.active ||
      event.origin !== this.active.checkoutOrigin ||
      event.source === null ||
      this.active.iframe.contentWindow === null ||
      event.source !== this.active.iframe.contentWindow ||
      !isMuPagMessage(event.data)
    ) {
      return;
    }
    if (event.data.type === "mupag:loaded") {
      this.active.loaded = true;
      return;
    }
    if (event.data.type === "mupag:payment_completed") {
      const charge = event.data.charge ?? event.data.payload?.charge ?? ({ status: "paid", ...event.data.payload } as Charge);
      this.active.callbacks.onSuccess?.(charge);
      this.close();
      return;
    }
    if (event.data.type === "mupag:close") {
      this.active.callbacks.onClose?.();
      this.close();
      return;
    }
    if (event.data.type === "mupag:error") {
      this.active.callbacks.onError?.(event.data.error ?? event.data.payload ?? { code: "checkout_error", message: "Checkout error." });
    }
  }
}

export function initDataAttributes(root: ParentNode = document): void {
  const buttons = root.querySelectorAll<HTMLElement>("[data-mupag-widget]");
  buttons.forEach((button) => {
    if (button.dataset[DATA_BOUND] === "true") {
      return;
    }
    button.dataset[DATA_BOUND] = "true";
    button.addEventListener("click", () => {
      try {
        const options = dataOptions(button);
        const theme: MuPagTheme = {};
        if (button.dataset.primaryColor) {
          theme.primaryColor = button.dataset.primaryColor;
        }
        if (button.dataset.accentColor) {
          theme.accentColor = button.dataset.accentColor;
        }
        if (button.dataset.fontFamily) {
          theme.fontFamily = button.dataset.fontFamily;
        }
        if (Object.keys(theme).length > 0) {
          options.theme = theme;
        }
        const mupag = new MuPag(options);
        void mupag.openCheckout(dataCheckout(button)).catch((error: unknown) => dispatchWidgetError(button, error));
      } catch (error) {
        dispatchWidgetError(button, error);
      }
    });
  });
}

function dispatchWidgetError(button: HTMLElement, error: unknown): void {
  button.dispatchEvent(
    new CustomEvent("mupag:error", {
      detail: { code: "checkout_failed", message: safeErrorMessage(error) },
    }),
  );
}

function validateOptions(options: MuPagOptions): void {
  if (!options || (options.environment !== "test" && options.environment !== "prd")) {
    throw new Error("environment must be explicitly test or prd.");
  }
  const expectedPrefix = options.environment === "test" ? "pk_test_" : "pk_prd_";
  if (
    typeof options.publishableKey !== "string" ||
    !options.publishableKey.startsWith(expectedPrefix) ||
    options.publishableKey.length > 512 ||
    !/^[\x21-\x7e]+$/.test(options.publishableKey)
  ) {
    throw new Error("publishableKey is invalid or does not match the selected environment.");
  }
  boundedInteger(options.iframeLoadTimeoutMs, 1, 120_000, "iframeLoadTimeoutMs");
  boundedInteger(options.requestTimeoutMs, 1, 120_000, "requestTimeoutMs");
  boundedInteger(options.maxResponseBytes, 1, 4 * 1024 * 1024, "maxResponseBytes");
  validateTheme(options.theme);
}

function allowedOrigin(value: string, canonical: string, environment: MuPagEnvironment, field: string): string {
  let url: URL;
  try {
    url = new URL(value);
  } catch {
    throw new Error(`${field} is invalid.`);
  }
  const loopback = environment === "test" && ["localhost", "127.0.0.1", "[::1]"].includes(url.hostname);
  const isCanonical = url.origin === new URL(canonical).origin;
  const explicitCheckoutDomain = field === "checkoutBaseUrl" && url.protocol === "https:" && !isIpAddress(url.hostname);
  if (
    (!isCanonical && !loopback && !explicitCheckoutDomain) ||
    (url.protocol !== "https:" && !(loopback && url.protocol === "http:")) ||
    url.username ||
    url.password ||
    (url.pathname !== "" && url.pathname !== "/") ||
    url.search ||
    url.hash
  ) {
    throw new Error(`${field} is not an allowed origin.`);
  }
  return trimSlash(url.origin);
}

function isIpAddress(hostname: string): boolean {
  return /^\d{1,3}(?:\.\d{1,3}){3}$/.test(hostname) || hostname.includes(":");
}

function validateCheckout(body: Omit<CheckoutParams, keyof MuPagCallbacks | "idempotencyKey">, environment: MuPagEnvironment): void {
  onlyKeys(
    body,
    [
      "items",
      "success_url",
      "cancel_url",
      "customer_id",
      "customer_data",
      "allowed_payment_methods",
      "affiliate_code",
      "coupon_id",
      "utm_params",
      "expires_in_minutes",
      "metadata",
      "collect_shipping_address",
      "delivery_type",
      "allow_coupons",
    ],
    "checkout",
  );
  if (!Array.isArray(body.items) || body.items.length < 1 || body.items.length > 100) {
    throw new Error("Checkout items must contain between 1 and 100 entries.");
  }
  let total = 0;
  for (const item of body.items) {
    onlyKeys(item, ["name", "quantity", "unit_amount_cents"], "checkout item");
    text(item.name, 200, "item.name");
    rejectPANLikeOptionalText(item.name, "item.name");
    integer(item.quantity, 1, 1_000_000, "item.quantity");
    integer(item.unit_amount_cents, 100, MAX_MONEY_CENTS, "item.unit_amount_cents");
    const lineTotal = item.quantity * item.unit_amount_cents;
    if (!Number.isSafeInteger(lineTotal) || lineTotal > MAX_MONEY_CENTS) {
      throw new Error("Checkout item total is outside the supported range.");
    }
    total += lineTotal;
    if (!Number.isSafeInteger(total) || total > MAX_MONEY_CENTS) {
      throw new Error("Checkout total is outside the supported range.");
    }
  }
  redirectUrl(body.success_url, environment, "success_url");
  redirectUrl(body.cancel_url, environment, "cancel_url");
  if (body.customer_id !== undefined && body.customer_data !== undefined) {
    throw new Error("customer_id and customer_data are mutually exclusive.");
  }
  if (body.customer_id !== undefined && !uuid(body.customer_id)) {
    throw new Error("customer_id must be a UUID.");
  }
  rejectPANLikeOptionalText(body.customer_id, "customer_id");
  if (body.customer_data !== undefined) {
    onlyKeys(body.customer_data, ["name", "email", "document", "phone"], "customer_data");
    optionalText(body.customer_data.name, 200, "customer_data.name");
    rejectPANLikeOptionalText(body.customer_data.name, "customer_data.name");
    optionalText(body.customer_data.email, 320, "customer_data.email");
    rejectPANLikeOptionalText(body.customer_data.email, "customer_data.email");
    optionalText(body.customer_data.document, 64, "customer_data.document");
    optionalText(body.customer_data.phone, 32, "customer_data.phone");
  }
  if (
    body.allowed_payment_methods !== undefined &&
    (!Array.isArray(body.allowed_payment_methods) ||
      body.allowed_payment_methods.length < 1 ||
      body.allowed_payment_methods.length > 2 ||
      new Set(body.allowed_payment_methods).size !== body.allowed_payment_methods.length ||
      body.allowed_payment_methods.some((method) => method !== "pix" && method !== "credit_card"))
  ) {
    throw new Error("allowed_payment_methods is invalid.");
  }
  optionalText(body.affiliate_code, 128, "affiliate_code");
  rejectPANLikeOptionalText(body.affiliate_code, "affiliate_code");
  if (body.coupon_id !== undefined && !uuid(body.coupon_id)) throw new Error("coupon_id must be a UUID.");
  if (body.allow_coupons === false && body.coupon_id !== undefined) {
    throw new Error("coupon_id cannot be used when allow_coupons is false.");
  }
  if (body.expires_in_minutes !== undefined) integer(body.expires_in_minutes, 1, 24 * 60, "expires_in_minutes");
  if (body.metadata !== undefined) jsonObject(body.metadata, "metadata");
  if (body.utm_params !== undefined) jsonObject(body.utm_params, "utm_params");
  optionalText(body.delivery_type, 64, "delivery_type");
  rejectPANLikeOptionalText(body.delivery_type, "delivery_type");
  optionalBoolean(body.collect_shipping_address, "collect_shipping_address");
  optionalBoolean(body.allow_coupons, "allow_coupons");
}

function onlyKeys(value: unknown, allowed: readonly string[], field: string): asserts value is Record<string, unknown> {
  if (value === null || typeof value !== "object" || Array.isArray(value) || Object.getPrototypeOf(value) !== Object.prototype) {
    throw new Error(`${field} must be a plain object.`);
  }
  const allowlist = new Set(allowed);
  const unsupported = Object.keys(value).find((key) => !allowlist.has(key));
  if (unsupported !== undefined) {
    throw new Error(`${field} contains unsupported field ${unsupported}.`);
  }
}

function jsonObject(value: object, field: string): void {
  if (value === null || Array.isArray(value) || Object.getPrototypeOf(value) !== Object.prototype) {
    throw new Error(`${field} must be a plain JSON object.`);
  }
  const stack: Array<{ value: unknown; depth: number }> = [{ value, depth: 0 }];
  const seen = new WeakSet<object>();
  let nodes = 0;
  while (stack.length > 0) {
    const current = stack.pop();
    if (!current) break;
    nodes += 1;
    if (nodes > 10_000 || current.depth > 32) throw new Error(`${field} is too complex.`);
    if (current.value === null || typeof current.value === "boolean") continue;
    if (typeof current.value === "string") {
      if (containsPANLikeSequence(current.value)) throw new Error(`${field} contains a possible card number.`);
      continue;
    }
    if (typeof current.value === "number") {
      if (!Number.isFinite(current.value)) throw new Error(`${field} contains an invalid number.`);
      if (containsPANLikeSequence(JSON.stringify(current.value))) {
        throw new Error(`${field} contains a possible card number.`);
      }
      continue;
    }
    if (typeof current.value !== "object") throw new Error(`${field} contains a non-JSON value.`);
    if (seen.has(current.value)) throw new Error(`${field} contains a cycle.`);
    seen.add(current.value);
    for (const [key, child] of Object.entries(current.value)) {
      if (containsPANLikeSequence(key)) {
        throw new Error(`${field} contains a possible card number.`);
      }
      const normalized = key.toLowerCase();
      const compact = normalized.replace(/[^a-z0-9]/g, "");
      const sensitiveBase = compact
        .replace(/[0-9]+$/, "")
        .replace(/(cvv|cvc|csc|cid|cav)[0-9]+/g, "$1");
      if (
        ["__proto__", "prototype", "constructor"].includes(normalized)
        || ["pan", "cardnumber"].includes(compact)
          || ["cvv", "cvc", "cav"].some((token) =>
          ["", "value", "code", "number"].some((descriptor) => sensitiveBase.endsWith(token + descriptor))
        )
        || ["csc", "cid"].some((token) =>
          ["", "value", "code", "number"].some(
            (descriptor) =>
              sensitiveBase === token + descriptor
              || ["card", "amex", "americanexpress"].some((qualifier) =>
                sensitiveBase.endsWith(qualifier + token + descriptor)
              )
          )
        )
        || sensitiveBase.endsWith("cardidentificationnumber")
        || sensitiveBase.endsWith("cardsecuritynumber")
        || [
          "securitycode",
          "securityvalue",
          "verificationcode",
          "verificationnumber",
          "verificationvalue",
        ].some((suffix) => sensitiveBase.endsWith(suffix))
      ) {
        throw new Error(`${field} contains a forbidden field.`);
      }
      stack.push({ value: child, depth: current.depth + 1 });
    }
  }
}

function containsPANLikeSequence(value: string): boolean {
  let retainedDigits = "";

  for (const character of value) {
    if (character >= "0" && character <= "9") {
      retainedDigits = (retainedDigits + character).slice(-19);
      for (let length = 12; length <= retainedDigits.length; length += 1) {
        if (validPANSequence(retainedDigits.slice(-length))) return true;
      }
    } else if (/^\p{Nd}$/u.test(character)) {
      return true;
    } else if (/^[\s\p{P}\p{S}\p{M}\p{Cc}\p{Cf}]$/u.test(character)) {
      continue;
    } else {
      retainedDigits = "";
    }
  }
  return false;
}

function rejectPANLikeOptionalText(value: unknown, field: string): void {
  if (value !== undefined && (typeof value !== "string" || containsPANLikeSequence(value))) {
    throw new Error(`${field} contains a possible card number.`);
  }
}

function validPANSequence(digits: string): boolean {
  if (!/^[0-9]{12,19}$/.test(digits)) return false;
  let sum = 0;
  let doubleDigit = false;
  for (let index = digits.length - 1; index >= 0; index -= 1) {
    let digit = digits.charCodeAt(index) - 48;
    if (doubleDigit) {
      digit *= 2;
      if (digit > 9) digit -= 9;
    }
    sum += digit;
    doubleDigit = !doubleDigit;
  }
  return sum % 10 === 0;
}

function redirectUrl(value: string, environment: MuPagEnvironment, field: string): void {
  let url: URL;
  try {
    url = new URL(value);
  } catch {
    throw new Error(`${field} must be an absolute URL.`);
  }
  const loopback = environment === "test" && ["localhost", "127.0.0.1", "[::1]"].includes(url.hostname);
  if ((url.protocol !== "https:" && !(loopback && url.protocol === "http:")) || url.username || url.password || value.length > 2048) {
    throw new Error(`${field} must be a safe HTTPS URL.`);
  }
}

function checkoutResponse(value: unknown): { id: string; url: string; expires_at: string } {
  if (!value || typeof value !== "object" || Array.isArray(value)) throw new Error("Invalid checkout response.");
  const response = value as Record<string, unknown>;
  if (!uuid(response.id) || typeof response.url !== "string" || typeof response.expires_at !== "string") {
    throw new Error("Invalid checkout response.");
  }
  if (Number.isNaN(Date.parse(response.expires_at))) throw new Error("Invalid checkout response.");
  return { id: response.id, url: response.url, expires_at: response.expires_at };
}

function safeCheckoutUrl(value: string, sessionId: string, baseUrl: string): URL {
  let url: URL;
  try {
    url = new URL(value);
  } catch {
    throw new Error("Invalid checkout URL in API response.");
  }
  if (
    url.origin !== new URL(baseUrl).origin ||
    url.username ||
    url.password ||
    url.protocol !== new URL(baseUrl).protocol ||
    url.pathname !== `/c/${sessionId}` ||
    url.search ||
    url.hash
  ) {
    throw new Error("Invalid checkout URL in API response.");
  }
  return url;
}

async function readBoundedJson(response: Response, maximumBytes: number): Promise<unknown> {
  const length = response.headers.get("content-length");
  if (length !== null && (!/^\d+$/.test(length) || Number(length) > maximumBytes)) {
    throw new Error("Checkout response exceeds the configured limit.");
  }
  if (!response.body) return undefined;
  const reader = response.body.getReader();
  const chunks: Uint8Array[] = [];
  let total = 0;
  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      if (!value) continue;
      total += value.byteLength;
      if (total > maximumBytes) {
        await reader.cancel("response limit exceeded");
        throw new Error("Checkout response exceeds the configured limit.");
      }
      chunks.push(value);
    }
  } finally {
    reader.releaseLock();
  }
  const buffer = new Uint8Array(total);
  let offset = 0;
  for (const chunk of chunks) {
    buffer.set(chunk, offset);
    offset += chunk.byteLength;
  }
  const textBody = new TextDecoder().decode(buffer);
  if (textBody.trim().length === 0) return undefined;
  try {
    return JSON.parse(textBody) as unknown;
  } catch {
    throw new Error("Checkout response is not valid JSON.");
  }
}

function createIdempotencyKey(): string {
  const cryptoApi = globalThis.crypto;
  if (typeof cryptoApi?.randomUUID === "function") return `widget_${cryptoApi.randomUUID()}`;
  if (typeof cryptoApi?.getRandomValues !== "function") {
    throw new Error("A secure random generator is required to create an Idempotency-Key.");
  }
  const bytes = new Uint8Array(16);
  cryptoApi.getRandomValues(bytes);
  return `widget_${Array.from(bytes, (entry) => entry.toString(16).padStart(2, "0")).join("")}`;
}

function validateIdempotencyKey(value: string): string {
  if (value.length < 1 || value.length > 128 || !/^[\x21-\x7e]+$/.test(value)) {
    throw new Error("idempotencyKey must contain 1-128 visible ASCII characters.");
  }
  return value;
}

function dataOptions(button: HTMLElement): MuPagOptions {
  return {
    environment: button.dataset.environment as MuPagEnvironment,
    publishableKey: button.dataset.publishableKey ?? "",
    ...(button.dataset.checkoutBaseUrl ? { checkoutBaseUrl: button.dataset.checkoutBaseUrl } : {}),
    ...(button.dataset.apiBaseUrl ? { apiBaseUrl: button.dataset.apiBaseUrl } : {}),
    ...(button.dataset.locale ? { locale: button.dataset.locale as MuPagLocale | "auto" } : {}),
  };
}

function dataCheckout(button: HTMLElement): CheckoutParams {
  return {
    items: [
      {
        name: button.dataset.itemName ?? "",
        quantity: Number(button.dataset.quantity ?? "1"),
        unit_amount_cents: Number(button.dataset.unitAmountCents ?? ""),
      },
    ],
    success_url: button.dataset.successUrl ?? "",
    cancel_url: button.dataset.cancelUrl ?? "",
  };
}

function validateTheme(theme: MuPagTheme | undefined): void {
  if (!theme) return;
  if (theme.primaryColor !== undefined && (!cssSupports("color", theme.primaryColor) || /url\s*\(/i.test(theme.primaryColor))) {
    throw new Error("theme.primaryColor is invalid.");
  }
  if (theme.accentColor !== undefined && (!cssSupports("color", theme.accentColor) || /url\s*\(/i.test(theme.accentColor))) {
    throw new Error("theme.accentColor is invalid.");
  }
  if (theme.borderRadius !== undefined && (!cssSupports("border-radius", theme.borderRadius) || /url\s*\(/i.test(theme.borderRadius))) {
    throw new Error("theme.borderRadius is invalid.");
  }
  if (theme.fontFamily !== undefined && !/^[A-Za-z0-9 ,_'"-]{1,200}$/.test(theme.fontFamily)) {
    throw new Error("theme.fontFamily is invalid.");
  }
}

function cssSupports(property: string, value: string): boolean {
  return typeof globalThis.CSS?.supports === "function" && globalThis.CSS.supports(property, value);
}

function boundedInteger(value: number | undefined, minimum: number, maximum: number, field: string): void {
  if (value !== undefined) integer(value, minimum, maximum, field);
}

function integer(value: number, minimum: number, maximum: number, field: string): void {
  if (!Number.isSafeInteger(value) || value < minimum || value > maximum) throw new Error(`${field} is invalid.`);
}

function text(value: unknown, maximum: number, field: string): void {
  if (typeof value !== "string" || value.trim().length < 1 || value.length > maximum || /[\x00-\x1f\x7f]/.test(value)) {
    throw new Error(`${field} is invalid.`);
  }
}

function optionalText(value: unknown, maximum: number, field: string): void {
  if (value !== undefined) text(value, maximum, field);
}

function optionalBoolean(value: boolean | undefined, field: string): void {
  if (value !== undefined && typeof value !== "boolean") throw new Error(`${field} is invalid.`);
}

function uuid(value: unknown): value is string {
  return typeof value === "string" && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value);
}

function safeErrorMessage(error: unknown): string {
  const message = error instanceof Error ? error.message : "Checkout failed.";
  return message.replace(/[\r\n\t]+/g, " ").slice(0, 256);
}

export const version = VERSION;
